import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Footer from '../components/Footer';
import GuestCheckoutDialog from '../components/GuestCheckoutDialog';
import ExitIntentDialog from '../components/ExitIntentDialog';
import CountdownBar from '../components/landing/CountdownBar';
import ProblemSection from '../components/landing/ProblemSection';
import SolutionSection from '../components/landing/SolutionSection';
import PriceSection from '../components/landing/PriceSection';
import TestimonialStrip from '../components/landing/TestimonialStrip';
import OutcomesSection from '../components/landing/OutcomesSection';
import FaqSection from '../components/landing/FaqSection';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage({ user, setUser }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ email: '', password: '', name: '' });
  const [loading, setLoading] = useState(false);
  const [testimonials, setTestimonials] = useState([]);
  const [flagshipCourse, setFlagshipCourse] = useState(null);
  const [checkoutItem, setCheckoutItem] = useState(null);
  const [aboutImage, setAboutImage] = useState(null);
  const [showStickyCta, setShowStickyCta] = useState(false);

  React.useEffect(() => {
    const onScroll = () => setShowStickyCta(window.scrollY > window.innerHeight * 0.8);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  React.useEffect(() => {
    fetchData();
    if (searchParams.get('login') === 'true') {
      setShowAuth(true);
      setIsLogin(true);
    }
    if (searchParams.get('register') === 'true') {
      setShowAuth(true);
      setIsLogin(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const fetchData = async () => {
    try {
      const [testimonialsRes, coursesRes, aboutRes] = await Promise.all([
        fetch(`${API}/testimonials`),
        fetch(`${API}/courses`),
        fetch(`${API}/about`)
      ]);
      if (testimonialsRes.ok) {
        setTestimonials(await testimonialsRes.json());
      }
      if (coursesRes.ok) {
        const courses = await coursesRes.json();
        setFlagshipCourse(courses.filter(c => !c.is_subscription)[0] || courses[0] || null);
      }
      if (aboutRes.ok) {
        const about = await aboutRes.json();
        if (about?.image_file) setAboutImage(`${API}/about/image/${about.image_file}`);
      }
    } catch (error) {
      console.error('Failed to fetch data');
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : { email: formData.email, password: formData.password, name: formData.name };

      const response = await fetch(`${API}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (response.ok) {
        localStorage.setItem('token', data.token);
        setUser(data.user);
        toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
        setShowAuth(false);
        navigate(data.user.role === 'admin' ? '/admin' : '/dashboard');
      } else {
        toast.error(data.detail || 'Authentication failed');
      }
    } catch (error) {
      toast.error('Connection error. Please check your internet and try again.');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (user) {
      navigate(user.role === 'admin' ? '/admin' : '/dashboard');
    }
  }, [user, navigate]);

  const scrollToOffer = () => document.getElementById('offer')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white">
      <nav className="fixed top-0 w-full z-50 backdrop-blur-md bg-[#0B0B0B]/85 border-b border-white/5">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-xl sm:text-2xl font-bold tracking-tight"
            style={{ fontFamily: 'Clash Display' }}
          >
            Move<span className="text-[#C6A97C]">More</span>
          </motion.div>
          <div className="flex gap-2 sm:gap-4 items-center">
            <Button
              data-testid="about-nav-btn"
              onClick={() => navigate('/about')}
              className="bg-transparent hover:bg-white/5 text-white rounded-full px-3 sm:px-5 text-sm sm:text-base hidden sm:inline-flex"
            >
              About
            </Button>
            <Button
              data-testid="courses-nav-btn"
              onClick={() => navigate('/courses')}
              className="bg-transparent hover:bg-white/5 text-white rounded-full px-3 sm:px-6 text-sm sm:text-base hidden sm:inline-flex"
            >
              Browse Courses
            </Button>
            <Button
              data-testid="get-started-btn"
              onClick={() => setShowAuth(true)}
              className="bg-[#D9C7A3] text-[#0B0B0B] hover:bg-[#C6A97C] rounded-full px-4 sm:px-8 py-2 sm:py-6 text-sm sm:text-base font-bold transition-transform hover:scale-105 active:scale-95"
            >
              Get Started <ArrowRight className="ml-1 sm:ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </nav>

      <section className="pt-20 sm:pt-24 bg-[#0B0B0B]" data-testid="hero-section">
        <div className="container mx-auto max-w-6xl px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-center py-12 sm:py-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="text-xs uppercase tracking-widest text-[#C6A97C] mb-6 font-semibold">Treat the system. Not the symptom.</div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-none tracking-tight" style={{ fontFamily: 'Clash Display' }}>
                Rebuild Your Body.
                <br />
                <span className="text-[#C6A97C]">Reclaim Your Life.</span>
              </h1>
              <p className="text-base sm:text-lg text-gray-400 mb-8 leading-relaxed max-w-lg">
                I help active people overcome pain and injury at the root cause — so you can move better, train harder and live without limitation.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button
                  data-testid="hero-cta-btn"
                  onClick={scrollToOffer}
                  className="bg-[#D9C7A3] text-[#0B0B0B] hover:bg-[#C6A97C] rounded-full px-8 sm:px-10 py-6 sm:py-7 text-base sm:text-lg font-bold transition-transform hover:scale-105 active:scale-95"
                >
                  Start Your Recovery
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <a
                  href="https://wa.me/27822532520?text=Hi%2C%20I'd%20like%20to%20book%20a%20call"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-book-call-btn"
                  className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white/50 hover:bg-white/5 text-white rounded-full px-8 sm:px-10 py-3 sm:py-3.5 text-base sm:text-lg font-semibold transition-all"
                >
                  <Phone className="w-5 h-5" /> Book a Call
                </a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="rounded-2xl overflow-hidden"
            >
              <img
                src={aboutImage || '/images/hero-athlete.jpg'}
                alt="Brandon — your MoveMore coach"
                className="w-full aspect-[4/5] sm:aspect-[3/4] lg:aspect-[4/5] object-cover"
                data-testid="hero-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <CountdownBar />
      <ProblemSection />
      <SolutionSection course={flagshipCourse} />
      <PriceSection course={flagshipCourse} onBuy={setCheckoutItem} />
      <TestimonialStrip testimonials={testimonials} />
      <OutcomesSection />
      <FaqSection />

      <Footer />

      <GuestCheckoutDialog item={checkoutItem} onClose={() => setCheckoutItem(null)} />
      <ExitIntentDialog course={flagshipCourse} onClaim={setCheckoutItem} />

      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent data-testid="auth-dialog" className="bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-[#0B0B0B]" style={{ fontFamily: 'Clash Display' }}>
              {isLogin ? 'Welcome Back' : 'Create Account'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAuth} className="space-y-4">
            {!isLogin && (
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  data-testid="auth-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required={!isLogin}
                  className="mt-1"
                />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="auth-email-input"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="auth-password-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="mt-1"
              />
            </div>
            <Button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={loading}
              className="w-full bg-[#0B0B0B] text-white hover:bg-[#2a2a2a] rounded-full py-6"
            >
              {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Create Account'}
            </Button>
          </form>
          <div className="text-center mt-4">
            <button
              data-testid="auth-toggle-btn"
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#0B0B0B] hover:underline"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AnimatePresence>
        {showStickyCta && (
          <motion.button
            key="sticky-offer-cta"
            type="button"
            onClick={scrollToOffer}
            data-testid="sticky-offer-btn"
            initial={{ opacity: 0, y: 24, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="fixed bottom-5 right-5 sm:bottom-7 sm:right-7 z-40 inline-flex items-center gap-2 bg-[#D9C7A3] hover:bg-[#C6A97C] text-[#0B0B0B] rounded-full pl-5 pr-6 py-3.5 sm:pl-6 sm:pr-7 sm:py-4 text-sm sm:text-base font-bold shadow-2xl shadow-black/40 ring-2 ring-white/10"
            style={{ fontFamily: 'Clash Display' }}
          >
            Get Instant Access — $49
            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
